// CausalPicker — selección de causal en dos niveles (categoría madre →
// causal específica). Extraído de ParoModal sin cambios de comportamiento:
// al cambiar la madre se limpia la específica, y la específica solo aparece
// cuando la madre tiene hijas.
//
// Controlado: el padre guarda ambos ids. Lo usan ParoModal (captura L2) y el
// diálogo de completar la causal de un paro de máquina.

import type { CausalParo, CausalParoTreeNode } from 'schemas';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { useTouchTargets } from '../../components/ui/touch-targets';

export interface CausalPickerProps {
  causales: CausalParoTreeNode[];
  causalMadreId: string;
  causalId: string;
  onCausalMadreChange: (id: string) => void;
  onCausalChange: (id: string) => void;
  /** Prefijo de los ids de los controles (default `paro`, el histórico de L2). */
  idPrefix?: string;
}

export function CausalPicker({
  causales,
  causalMadreId,
  causalId,
  onCausalMadreChange,
  onCausalChange,
  idPrefix = 'paro',
}: CausalPickerProps) {
  const touch = useTouchTargets();
  const selectClass = touch ? 'h-11' : undefined;
  const madre = causales.find((m) => m.id === causalMadreId);
  const especificas = madre?.hijas ?? [];
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-madre`}>Categoría</Label>
        <Select
          id={`${idPrefix}-madre`}
          value={causalMadreId}
          className={selectClass}
          onChange={(e) => {
            onCausalMadreChange(e.target.value);
            onCausalChange('');
          }}
        >
          <option value="">— Elija —</option>
          {causales.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nombre}
            </option>
          ))}
        </Select>
      </div>

      {especificas.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-especifica`}>Causal específica</Label>
          <Select
            id={`${idPrefix}-especifica`}
            value={causalId}
            className={selectClass}
            onChange={(e) => onCausalChange(e.target.value)}
          >
            <option value="">— Elija —</option>
            {especificas.map((c: CausalParo) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
                {c.requiere_observacion ? ' (req. observación)' : ''}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
    </>
  );
}

/** Causal específica elegida (o null) — para saber si exige observación. */
export function causalEspecificaDe(
  causales: CausalParoTreeNode[],
  causalMadreId: string,
  causalId: string,
): CausalParo | null {
  const madre = causales.find((m) => m.id === causalMadreId);
  return madre?.hijas.find((c) => c.id === causalId) ?? null;
}
